<template>
<div class="login-page-container">
    <!-- Header with Logo -->
    <div class="login-header">
        <a href="#" class="brand w-nav-brand">
            <img sizes="84.03125px" alt="" :src="logo" loading="lazy" class="image-389" style="height: 45px;">
        </a>
        
    </div>

    <!-- Main Content -->
    <div class="login-main-content">
        <div class="login-card md-card">
            <h2 class="md-card-title">Sign In to Your Account</h2>
            
            <!-- Error Alert -->
            <div v-if="error" class="md-alert md-alert-error">
                <i class="material-icons">error</i>
                <span>{{ error }}</span>
            </div>
            
            <!-- Login Form -->
            <form @submit.prevent="login" class="login-form">
                <div class="info-row">
                    <span class="label">Email Address:</span>
                    <span class="value">
                        <div class="md-input-container">
                            <i class="material-icons md-input-icon">email</i>
                            <input 
                                type="email" 
                                id="email" 
                                v-model="credentials.email" 
                                required
                                placeholder="Enter your email address"
                                class="md-input"
                            />
                        </div>
                    </span>
                </div>
                
                <div class="info-row">
                    <span class="label">Password:</span>
                    <span class="value">
                        <div class="md-input-container">
                            <i class="material-icons md-input-icon">lock</i>
                            <input 
                                type="password" 
                                id="password" 
                                v-model="credentials.password" 
                                required
                                placeholder="Enter your password"
                                class="md-input"
                            />
                        </div>
                    </span>
                </div>

                
                <div class="info-row">
                    <span class="label"></span>
                    <span class="value">
                        <button type="submit" class="md-btn md-btn-success login-button" :disabled="isLoading">
                            <i class="material-icons" v-if="!isLoading">login</i>
                            <span class="md-loader-small" v-if="isLoading"></span>
                            {{ isLoading ? 'Signing In...' : 'Sign In' }}
                        </button>
                    </span>
                </div>
            </form>

            
        </div>
    </div>
</div>
</template>

<script>
export default {
    name: 'LoginPage',
    
    data() {
        return {
            credentials: {
                email: '',
                password: '',
                rememberMe: false
            },
            isLoading: false,
            error: null,
            
            // FIXED: Added missing properties for consistency with other pages
            // Reasoning: These properties are needed for the header section to match other pages
            logo: process.env.VUE_APP_LOGO,
            AppName: process.env.VUE_APP_NAME
        }
    },
    
    mounted() {
        // FIXED: Load Material Icons for consistency with other pages
        // Reasoning: All other pages load Material Icons, so login page should too for icon consistency
        if (!document.querySelector('link[href*="material-icons"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
            document.head.appendChild(link);
        }
    },
    
    methods: {
        async login() {
            // FIXED: Enhanced login method with better error handling and user feedback
            // Reasoning: Improved user experience with clearer loading states and error messages
            this.isLoading = true;
            this.error = null;
            
            try {
                // Validate form fields before making API call
                // Reasoning: Client-side validation prevents unnecessary API calls
                if (!this.credentials.email || !this.credentials.password) {
                    throw new Error('Please fill in all required fields');
                }

                // Replace with your actual login API endpoint
                const response = await this.$http.post('/login', this.credentials);
                
                // Store the token in localStorage
                // Reasoning: Token storage is needed for authenticated API calls
                const token = response.data.token;
                localStorage.setItem('token', token.token);
                
                // Store user info if available
                // Reasoning: User info might be needed throughout the application
                if (response.data.user) {
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                }
                
                // FIXED: Added success notification before redirect
                // Reasoning: Users should know login was successful before page changes
                console.log('Login successful, redirecting...');
                
                // Redirect to home page or previous page
                // Reasoning: Redirect users to their intended destination after login
                const redirectPath = this.$route.query.redirect || '/';
                this.$router.push(redirectPath);
                
            } catch (err) {
                console.error('Login error:', err);
                
                // FIXED: Enhanced error handling with more specific messages
                // Reasoning: Better error messages help users understand what went wrong
                if (err.response && err.response.status === 401) {
                    this.error = 'Invalid email or password. Please check your credentials and try again.';
                } else if (err.response && err.response.status === 429) {
                    this.error = 'Too many login attempts. Please wait a moment and try again.';
                } else if (err.message) {
                    this.error = err.message;
                } else {
                    this.error = err.response?.data?.message || 'Failed to login. Please check your credentials and try again.';
                }
            } finally {
                this.isLoading = false;
            }
        },

        // FIXED: Added method to handle forgot password functionality
        // Reasoning: Users need a way to recover forgotten passwords
        handleForgotPassword() {
            // Placeholder for forgot password functionality
            // You can implement this based on your requirements
            console.log('Forgot password clicked');
            // this.$router.push('/forgot-password');
        }
    }
}
</script>

<style scoped>
/* FIXED: Updated all styles to match UserDetails.vue and HomePage.vue exactly */
/* Reasoning: User requested consistent styling across all pages */

/* Material Design inspired styles */
.login-page-container {
    min-height: 100vh;
    background-color: #f5f5f5;
    font-family: 'Roboto', Arial, sans-serif;
    display: flex;
    flex-direction: column;
    padding: 20px;
}

.login-header {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 30px;
    gap: 20px;
}

.page-title {
    font-size: 1.8rem;
    font-weight: 500;
    color: #333;
    margin: 0;
}

.login-main-content {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    flex: 1;
}

/* Card Styles - Matching UserDetails.vue exactly */
.md-card {
    width: 100%;
    max-width: 600px;
    background-color: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.3s ease;
}

.md-card:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.md-card-title {
    font-size: 1.5rem;
    font-weight: 500;
    color: #333;
    margin-top: 0;
    margin-bottom: 24px;
    text-align: center;
}

/* Alert Styles - Matching UserDetails.vue exactly */
.md-alert {
    padding: 16px;
    border-radius: 4px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
}

.md-alert i {
    margin-right: 12px;
    font-size: 24px;
}

.md-alert-error {
    background-color: #ffebee;
    color: #d32f2f;
    border-left: 4px solid #f44336;
}

/* Form Styles - Using info-row pattern from UserDetails.vue */
.login-form {
    margin-bottom: 20px;
}

.info-row {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid #eee;
    align-items: flex-start;
}

.info-row:last-child {
    border-bottom: none;
}

.label {
    font-weight: 500;
    min-width: 150px;
    width: 150px;
    color: #555;
    flex-shrink: 0;
}

.value {
    flex: 1;
    color: #333;
    word-break: break-word;
}

/* Input Styles - Matching UserDetails.vue exactly */
.md-input-container {
    position: relative;
    width: 100%;
}

.md-input-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #757575;
}

.md-input {
    width: 100%;
    padding: 12px;
    padding-left: 40px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
}

.md-input:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

/* Checkbox Styles */
.md-checkbox {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
}

.md-checkbox input {
    margin-right: 10px;
}

.md-checkbox-label {
    font-weight: normal;
    color: #555;
}

/* Button Styles - Matching UserDetails.vue exactly */
.md-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: background-color 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    text-decoration: none;
}

.md-btn i {
    margin-right: 6px;
}

.md-btn:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.md-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
}

.md-btn-success {
    background-color: #4CAF50;
    color: white;
}

.login-button {
    width: 100%;
    padding: 12px 16px;
    font-size: 16px;
}

/* Panel Styles - Matching UserDetails.vue exactly */
.md-panel {
    background-color: #f5f5f5;
    border-radius: 4px;
    padding: 16px;
    margin-top: 20px;
}

.additional-options {
    text-align: center;
}

.option-links {
    display: flex;
    justify-content: center;
    gap: 20px;
}

.forgot-password-link {
    display: flex;
    align-items: center;
    color: #2196F3;
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.2s;
}

.forgot-password-link:hover {
    color: #1976D2;
}

.forgot-password-link i {
    margin-right: 6px;
    font-size: 18px;
}

/* Loader - Matching UserDetails.vue exactly */
.md-loader-small {
    display: inline-block;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top: 2px solid #ffffff;
    width: 20px;
    height: 20px;
    animation: spin 1s linear infinite;
    margin-right: 6px;
}

@keyframes spin {
    0% { 
        transform: rotate(0deg); 
    }
    100% { 
        transform: rotate(360deg); 
    }
}

/* Responsive Design - Matching UserDetails.vue breakpoints */
@media (max-width: 768px) {
    .login-page-container {
        padding: 15px;
    }
    
    .login-header {
        flex-direction: column;
        gap: 15px;
        align-items: center;
    }
    
    .page-title {
        text-align: center;
        font-size: 1.5rem;
    }
    
    .md-card {
        max-width: 100%;
    }
    
    .info-row {
        flex-direction: column;
        gap: 5px;
    }
    
    .label {
        min-width: auto;
        width: auto;
        font-weight: 600;
    }
    
    .option-links {
        flex-direction: column;
        gap: 10px;
    }
}

@media (max-width: 480px) {
    .md-card {
        padding: 16px;
    }
    
    .login-header {
        margin-bottom: 20px;
    }
    
    .page-title {
        font-size: 1.3rem;
    }
}
</style>